'use client';

import { Button, Card, CardContent, Input, Logo } from '@lectura/ui';
import { Mail } from 'lucide-react';
import Link from 'next/link';
import { useState } from 'react';

/**
 * Elfelejtett jelszó — jelszó-reset link-kérő oldal.
 *
 * A backend MINDIG sikerrel válaszol (timing-védelem). DEV-modeban a válasz
 * tartalmazza a `reset_token` mezőt — egyszerűbb fejlesztői flow miatt
 * automatikusan a `/reset/<token>` oldalra ugrunk. PROD-ban a felhasználónak
 * az emailjén át érkezik a link.
 */
export default function ForgotPage() {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [devToken, setDevToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch('/api/identity/auth/password-reset/request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Tenant-Id': 'default' },
        body: JSON.stringify({ email }),
      });
      if (!res.ok) {
        setError('A kérés most nem dolgozható fel. Próbáld újra.');
        return;
      }
      const data = (await res.json()) as { reset_token?: string | null };
      setSubmitted(true);
      if (data.reset_token) setDevToken(data.reset_token);
    } catch {
      setError('A kiszolgáló nem érhető el.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="bg-brand-gradient flex min-h-screen items-center justify-center p-6">
      <Card className="w-full max-w-md bg-white">
        <CardContent className="p-8">
          <div className="mb-6 flex justify-center">
            <Logo />
          </div>
          <h1 className="mb-1 text-center font-display text-2xl font-bold">Új jelszó kérése</h1>
          <p className="mb-6 text-center text-sm text-[var(--color-foreground-muted)]">
            Add meg az email címed — küldünk egy egyszer-felhasználható linket
            a jelszó beállításához. A link 30 percig érvényes.
          </p>

          {!submitted ? (
            <form className="flex flex-col gap-3" onSubmit={handleSubmit}>
              <Input
                type="email"
                autoComplete="email"
                required
                placeholder="email@példa.com"
                leftSlot={<Mail className="size-4" />}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              {error ? (
                <p className="text-sm text-red-600" role="alert">
                  {error}
                </p>
              ) : null}
              <Button type="submit" loading={submitting} size="lg" className="mt-2">
                Küldd a linket
              </Button>
            </form>
          ) : (
            <div className="space-y-3">
              <p className="rounded-lg bg-green-50 p-3 text-sm text-green-900">
                Ha az email cím létezik a rendszerben, percek alatt megérkezik a
                jelszó-átállító link.
              </p>
              {devToken ? (
                <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">
                  <p className="mb-2 font-semibold">DEV mód — közvetlen link:</p>
                  <Link
                    href={`/reset/${devToken}` as const}
                    className="break-all font-mono underline"
                  >
                    /reset/{devToken}
                  </Link>
                </div>
              ) : null}
            </div>
          )}

          <p className="mt-6 text-center text-sm text-[var(--color-foreground-muted)]">
            Eszedbe jutott?{' '}
            <Link href="/login" className="text-brand-600 hover:underline">
              Vissza a belépéshez
            </Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
